import { useMemo, useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { fmt } from '@/utils/formatters';
import type { DrillTable } from '@/types/dashboard';

interface CampusPersonRankProps {
  drill: DrillTable;
  onPersonClick?: (name: string) => void;
}

const LAYER_META = [
  { label: '1级末位', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)' },
  { label: '2级末位', color: '#84cc16', bg: 'rgba(132, 204, 22, 0.12)' },
  { label: '3级末位', color: '#eab308', bg: 'rgba(234, 179, 8, 0.12)' },
  { label: '4级末位', color: '#f97316', bg: 'rgba(249, 115, 22, 0.12)' },
  { label: '5级末位', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)' },
];

function toNumber(v: unknown): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function avg(arr: number[]): number {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function sum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

export default function CampusPersonRank({ drill, onPersonClick }: CampusPersonRankProps) {
  const rows = drill.data || [];
  const [expandedLayer, setExpandedLayer] = useState<number | null>(null);

  const {
    excludedCount,
    validCount,
    totalPeople,
    layers,
  } = useMemo(() => {
    const validRows = rows.filter((r) => toNumber(r['月总压仓金额万']) > 0);
    const excluded = rows.filter((r) => toNumber(r['月总压仓金额万']) === 0);
    // 基于5个字段的综合排名分层：综合排名越小表现越好，因此按综合排名升序后前段为1级（优秀），后段为5级（末位）
    const sorted = [...validRows].sort((a, b) => toNumber(a['综合排名']) - toNumber(b['综合排名']));
    const total = sorted.length;
    const perLayer = total > 0 ? Math.ceil(total / 5) : 0;

    const layers = LAYER_META.map((meta, idx) => {
      const start = idx * perLayer;
      const end = Math.min(start + perLayer, total);
      const layerRows = sorted.slice(start, end);

      const completions = layerRows.map((r) => toNumber(r['压仓金额目标完成度']));
      const daysWithAmount = layerRows.map((r) => toNumber(r['有金额的压仓天数']));
      const storeCounts = layerRows.map((r) => toNumber(r['压仓网点数（有POS的网点数）']));
      const campusRatios = layerRows.map((r) => toNumber(r['校内网点压仓金额占比']));
      const gaoxinRatios = layerRows.map((r) => toNumber(r['高新趋占比']));

      return {
        meta,
        rows: layerRows,
        completion: avg(completions),
        daysWithAmount: avg(daysWithAmount),
        avgStoreCount: avg(storeCounts),
        totalStoreCount: sum(storeCounts),
        campusRatio: avg(campusRatios),
        gaoxinRatio: avg(gaoxinRatios),
        personCount: layerRows.length,
      };
    });

    return {
      excludedCount: excluded.length,
      validCount: validRows.length,
      totalPeople: rows.length,
      layers,
    };
  }, [rows]);

  const kpiCards = [
    { label: '不考核业务员数', value: excludedCount, unit: '人', color: '#94a3b8' },
    { label: '压仓记录数(本期间)', value: validCount, unit: '人', color: '#60a5fa' },
    { label: 'POS记录数', value: '-', unit: '', color: '#a78bfa' },
  ];

  return (
    <div className="campus-rank-section">
      <div className="section-title">人员月度累计汇总（点击人员下钻到每日明细）</div>

      <div className="campus-rank-kpis">
        {kpiCards.map((kpi) => (
          <div key={kpi.label} className="campus-rank-kpi">
            <div className="campus-rank-kpi-value" style={{ color: kpi.color }}>
              {typeof kpi.value === 'number' ? kpi.value.toLocaleString('zh-CN') : kpi.value}
            </div>
            <div className="campus-rank-kpi-label">{kpi.label}</div>
          </div>
        ))}
      </div>

      <div className="campus-rank-table-wrap">
        <table className="campus-rank-table">
          <thead>
            <tr>
              <th rowSpan={2} className="campus-rank-th-main">
                排名分层
              </th>
              <th colSpan={1} className="campus-rank-th-group">达成指标</th>
              <th colSpan={2} className="campus-rank-th-group">过程指标</th>
              <th colSpan={2} className="campus-rank-th-group">POS指标</th>
              <th colSpan={1} className="campus-rank-th-group">风险指标</th>
            </tr>
            <tr>
              <th>压仓金额目标完成度</th>
              <th>有金额的压仓天数</th>
              <th>压仓网点数</th>
              <th>校内网点压仓金额占比</th>
              <th>高新趋小计占压仓金额占比</th>
              <th>不达标日数</th>
            </tr>
          </thead>
          <tbody>
            {layers.map((layer, idx) => (
              <>
                <tr
                  key={layer.meta.label}
                  className="campus-rank-row"
                  onClick={() => setExpandedLayer(expandedLayer === idx ? null : idx)}
                >
                  <td>
                    <span
                      className="campus-rank-badge"
                      style={{
                        color: layer.meta.color,
                        backgroundColor: layer.meta.bg,
                        borderColor: layer.meta.color,
                      }}
                    >
                      {layer.meta.label}
                    </span>
                    <span className="campus-rank-count">{layer.personCount}人</span>
                  </td>
                  <td>{fmt(layer.completion, '')}%</td>
                  <td>{fmt(layer.daysWithAmount, '')}</td>
                  <td>{fmt(layer.avgStoreCount, '')}</td>
                  <td>{fmt(layer.campusRatio * 100, '')}%</td>
                  <td>{fmt(layer.gaoxinRatio * 100, '')}%</td>
                  <td className="campus-rank-na">-</td>
                </tr>
                {expandedLayer === idx && (
                  <tr className="campus-rank-detail-row">
                    <td colSpan={7}>
                      <div className="campus-rank-detail">
                        <div className="campus-rank-detail-title">
                          {layer.meta.label} 明细
                          <span className="campus-rank-detail-close" onClick={(e) => { e.stopPropagation(); setExpandedLayer(null); }}>
                            <ChevronUp size={16} />
                          </span>
                        </div>
                        <div className="campus-rank-detail-grid">
                          {layer.rows.map((row, ridx) => (
                            <div
                              key={ridx}
                              className="campus-rank-detail-item"
                              onClick={() => onPersonClick?.(String(row['执行人'] ?? row['人员'] ?? row['姓名'] ?? ''))}
                            >
                              <span className="campus-rank-detail-name">{String(row['执行人'] ?? row['人员'] ?? row['姓名'] ?? '')}</span>
                              <span className="campus-rank-detail-prov">{String(row['省办'] ?? '')}</span>
                              <span className="campus-rank-detail-amount">{fmt(row['月总压仓金额万'], '')}万</span>
                              <span className="campus-rank-detail-stores">{fmt(row['月度总网点数'], '')}网点</span>
                              <span className="campus-rank-detail-rank">综合排名{fmt(row['综合排名'], '')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {layers.length === 0 && (
              <tr>
                <td colSpan={7} className="campus-rank-empty">暂无数据</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="campus-rank-footer">
        共 {totalPeople.toLocaleString('zh-CN')} 人 · 点击排名分层可展开明细
      </div>
    </div>
  );
}
